import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, FileText, Calendar, History, Settings, LogOut, Menu, X, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';
import Chatbot from './Chatbot';

export default function Layout({ children, user, onLogout, token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const menuItems = [
    { path: '/', label: t('dashboard'), icon: LayoutDashboard, testId: 'nav-dashboard' },
    { path: '/bills', label: t('bills'), icon: FileText, testId: 'nav-bills' },
    { path: '/appointments', label: t('appointments'), icon: Calendar, testId: 'nav-appointments' },
    { path: '/address', label: t('change_address'), icon: MapPin, testId: 'nav-address' },
    { path: '/history', label: t('history'), icon: History, testId: 'nav-history' },
    { path: '/settings', label: t('settings'), icon: Settings, testId: 'nav-settings' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Top Header - Mobile - BIG CENTERED LOGO */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b-2 border-blue-100 shadow-md">
        <div className="flex items-center justify-center px-4 py-4">
          {/* BIG Centered Logo */}
          <h1 className="text-5xl font-black bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 bg-clip-text text-transparent tracking-tight drop-shadow-sm" data-testid="app-logo">
            {t('app_name')}
          </h1>
        </div>
        {/* Second row with notification and menu */}
        <div className="flex items-center justify-end px-4 pb-2">
          <div className="flex items-center gap-2">
            <NotificationBell token={token} />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors bg-slate-50"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        >
          <div 
            className="absolute top-[132px] left-0 right-0 mx-4 bg-white rounded-2xl shadow-2xl p-4 animate-slideIn max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    data-testid={item.testId}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive(item.path)
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                        : 'hover:bg-slate-100 text-slate-700'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}
              <button
                onClick={() => {
                  onLogout();
                  setMobileMenuOpen(false);
                }}
                data-testid="logout-button"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-all"
              >
                <LogOut className="h-5 w-5" />
                <span className="font-medium">{t('logout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-white/80 backdrop-blur-md border-r border-slate-200 z-40">
        <div className="p-6">
          {/* Large Centered Logo */}
          <div className="text-center mb-6">
            <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 bg-clip-text text-transparent tracking-tight" data-testid="app-logo-desktop">
              {t('app_name')}
            </h1>
          </div>
          
          {/* Notification Bell */}
          <div className="flex justify-center mb-6">
            <NotificationBell token={token} />
          </div>
          
          <div className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  data-testid={item.testId}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                    isActive(item.path)
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20'
                      : 'hover:bg-slate-100 text-slate-700'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="absolute bottom-6 left-6 right-6">
          <div className="bg-slate-100 rounded-xl p-4 mb-4">
            <p className="text-sm text-slate-600 mb-1">{t('welcome')}</p>
            <p className="font-semibold text-slate-900" data-testid="user-name">{user.full_name}</p>
          </div>
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="logout-button-desktop"
          >
            <LogOut className="h-5 w-5 mr-3" />
            {t('logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-24 lg:pt-0 min-h-screen">
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* Bottom Navigation - Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-slate-200 z-40">
        <div className="flex items-center justify-around py-2 px-2">
          {menuItems.slice(0, 4).map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`${item.testId}-mobile`}
                className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all ${
                  isActive(item.path)
                    ? 'text-blue-600'
                    : 'text-slate-600 hover:text-blue-600'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chatbot */}
      <Chatbot />
    </div>
  );
}